import React, { useState, useEffect } from "react";
import {
  getStorage,
  ref,
  listAll,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Button,
  Modal,
} from "react-bootstrap";

const FbPhotoGallery = ({ folderName }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState(null); // Stores the image URL for the modal
  const [showModal, setShowModal] = useState(false); // Controls modal visibility

  useEffect(() => {
    if (!folderName) return;

    const storage = getStorage();
    const folderRef = ref(storage, folderName);

    const fetchImages = () => {
      listAll(folderRef)
        .then((res) => {
          const promises = res.items.map((itemRef) =>
            getDownloadURL(itemRef).then((url) => ({
              name: itemRef.name,
              url,
              fullPath: itemRef.fullPath, // Save full path for deletion
            }))
          );

          return Promise.all(promises);
        })
        .then((imageData) => {
          setImages(imageData);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching images:", error);
          setLoading(false);
        });
    };

    fetchImages();

    // Set up a polling mechanism for real-time sensitivity
    const intervalId = setInterval(fetchImages, 5000); // Poll every 5 seconds

    return () => clearInterval(intervalId); // Cleanup on unmount
  }, [folderName]);

  const handleDelete = (image) => {
    const confirmation = window.confirm(
      `Are you sure you want to delete the image "${image.name}"?`
    );

    if (!confirmation) return;

    const storage = getStorage();
    const imageRef = ref(storage, image.fullPath);

    deleteObject(imageRef)
      .then(() => {
        alert(`Image "${image.name}" deleted successfully.`);
        setImages((prevImages) =>
          prevImages.filter((img) => img.name !== image.name)
        );
      })
      .catch((error) => {
        console.error("Error deleting image:", error);
        alert("Failed to delete the image.");
      });
  };

  const handleImageClick = (imageUrl) => {
    setModalImage(imageUrl); // Set the clicked image URL in state
    setShowModal(true); // Open the modal
  };

  const handleCloseModal = () => {
    setShowModal(false); // Close the modal
    setModalImage(null); // Clear the modal image
  };

  return (
    <Container className="mt-4">
      <h2 className="text-center">Photo Gallery</h2>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" />
          <p>Loading images...</p>
        </div>
      ) : (
        <Row>
          {images.map((image) => (
            <Col key={image.name} xs={6} sm={6} md={4} lg={3} className="mb-4">
              <Card>
                <Card.Img
                  variant="top"
                  src={image.url}
                  alt={image.name}
                  className="img-fluid"
                  style={{
                    height: "200px",
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                  onClick={() => handleImageClick(image.url)} // Open modal on click
                />
                <Card.Body>
                  <Card.Text className="text-truncate">{image.name}</Card.Text>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => handleDelete(image)}
                  >
                    Delete
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Fullscreen Modal */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        size="lg"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {modalImage && (
            <img
              src={modalImage}
              alt="Full-size preview"
              className="img-fluid"
              style={{ maxHeight: "80vh" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default FbPhotoGallery;
